import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import api from '../api/axiosInstance';
import './MarketScreener.css';

// ── Constants ─────────────────────────────────────────────────────────────────
const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const MONTHS_FULL = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];
const YEARS = [2024, 2025, 2026];
const TOP_N = 50;
const SPIKE_MULT = 1.5;

// collection key → { tag, label }
const COLLECTIONS = [
  { key: 'nifty50', tag: 'N50' },
  { key: 'fo',      tag: 'FO' },
  { key: 'cash',    tag: 'CASH' },
];
const COL_TAG = { nifty50: 'N50', fo: 'FO', cash: 'CASH' };
const COL_PRIORITY = ['nifty50', 'fo', 'cash'];
const sortCollections = (cols) =>
  [...new Set(cols)].sort((a, b) => COL_PRIORITY.indexOf(a) - COL_PRIORITY.indexOf(b));

const SCREENERS = [
  { key: 'daily',        icon: '📊', label: 'Daily Highest',   desc: 'Highest buying & selling on the last trading day.' },
  { key: 'weekly',       icon: '📈', label: 'Weekly Highest',  desc: 'Highest in the current week (Mon–Sun), not rolling.' },
  { key: 'monthly',      icon: '🗓️', label: 'Monthly Highest', desc: 'Highest for the full selected month.' },
  { key: 'dailySpike',   icon: '⚡', label: 'Daily Spike',     desc: "Last day's activity ≥1.5× the monthly average." },
  { key: 'weeklySpike',  icon: '🔥', label: 'Weekly Spike',    desc: 'Current week (Mon–Sun) ≥1.5× the monthly average.' },
  { key: 'peak',         icon: '🏔️', label: 'Peak Day',        desc: 'Month high/low day is the last trading day.' },
  { key: 'followup',     icon: '🔗', label: 'Continued 5-Day', desc: 'Same direction every day across the last 5 days.' },
  { key: 'wholeMonth',   icon: '📅', label: 'Whole Month',     desc: 'Same direction on every trading day of the month.' },
  { key: 'consistent75', icon: '🎯', label: '75%+ Days',       desc: 'Same direction on more than 75% of month days.' },
  { key: 'dateWise',     icon: '🔎', label: 'Date-wise',       desc: 'Highest for a specific selected date.' },
];

// ── Helpers ───────────────────────────────────────────────────────────────────
function formatNumber(num) {
  const abs = Math.abs(Math.round(num));
  const fmt = new Intl.NumberFormat('en-IN').format(abs);
  return num < 0 ? `-${fmt}` : fmt;
}

function parseVal(raw) {
  if (raw == null) return 0;
  if (raw.$numberDecimal) return parseFloat(raw.$numberDecimal);
  return parseFloat(raw || 0);
}

function toDateStr(d) {
  return d ? new Date(d).toISOString().split('T')[0] : '';
}

// Monday–Sunday range (as YYYY-MM-DD) of the calendar week containing dateStr.
function weekRange(dateStr) {
  const d = new Date(`${dateStr}T00:00:00Z`);
  const dow = d.getUTCDay(); // 0=Sun … 6=Sat
  const mon = new Date(d);
  mon.setUTCDate(d.getUTCDate() + (dow === 0 ? -6 : 1 - dow));
  const sun = new Date(mon);
  sun.setUTCDate(mon.getUTCDate() + 6);
  return { start: mon.toISOString().split('T')[0], end: sun.toISOString().split('T')[0] };
}

// ── Icons ─────────────────────────────────────────────────────────────────────
const TvIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24"
    fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
  </svg>
);
const CalIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24"
    fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

// ── Screener computation ───────────────────────────────────────────────────────
function buildScreener(key, md, dateFilter, filterCat) {
  const { rows, tradingDays } = md;
  if (!tradingDays.length) return { buying: [], selling: [] };

  const totalDays = tradingDays.length;
  const lastDay   = tradingDays[totalDays - 1];
  const last5     = tradingDays.slice(-5);
  const { start: wkStart, end: wkEnd } = weekRange(lastDay);
  const weekDays  = tradingDays.filter(d => d >= wkStart && d <= wkEnd);
  const buying = [];
  const selling = [];

  const add = (arr, r, value, meta) =>
    arr.push({ symbol: r.symbol, collections: r.collections, primary: r.primary, value, ...meta });

  for (const r of rows) {
    if (filterCat && filterCat !== 'all' && !r.collections.includes(filterCat)) continue;
    switch (key) {
      case 'daily': {
        const v = r.days[lastDay] || 0;
        if (v > 0) add(buying, r, v);
        else if (v < 0) add(selling, r, v);
        break;
      }
      case 'weekly': {
        let v = 0;
        for (const d of weekDays) v += r.days[d] || 0;
        if (v > 0) add(buying, r, v);
        else if (v < 0) add(selling, r, v);
        break;
      }
      case 'monthly': {
        if (r.total > 0) add(buying, r, r.total);
        else if (r.total < 0) add(selling, r, r.total);
        break;
      }
      case 'dailySpike': {
        const dayVal = r.days[lastDay] || 0;
        if (dayVal === 0) break;
        const monthlyAvg = r.total / totalDays;
        if (dayVal > 0) {
          const isReversal = monthlyAvg <= 0;
          const ratio = isReversal ? 999 : dayVal / Math.max(monthlyAvg, 0.001);
          if (isReversal || ratio >= SPIKE_MULT) add(buying, r, dayVal, { ratio: Math.min(ratio, 99), isReversal });
        } else {
          const isReversal = monthlyAvg >= 0;
          const ratio = isReversal ? 999 : Math.abs(dayVal) / Math.max(Math.abs(monthlyAvg), 0.001);
          if (isReversal || ratio >= SPIKE_MULT) add(selling, r, dayVal, { ratio: Math.min(ratio, 99), isReversal });
        }
        break;
      }
      case 'weeklySpike': {
        let v = 0;
        for (const d of weekDays) v += r.days[d] || 0;
        if (v === 0) break;
        const dailyRate  = v / (weekDays.length || 1);
        const monthlyAvg = r.total / totalDays;
        if (dailyRate > 0) {
          const isReversal = monthlyAvg <= 0;
          const ratio = isReversal ? 999 : dailyRate / Math.max(monthlyAvg, 0.001);
          if (isReversal || ratio >= SPIKE_MULT) add(buying, r, v, { ratio: Math.min(ratio, 99), isReversal });
        } else {
          const isReversal = monthlyAvg >= 0;
          const ratio = isReversal ? 999 : Math.abs(dailyRate) / Math.max(Math.abs(monthlyAvg), 0.001);
          if (isReversal || ratio >= SPIKE_MULT) add(selling, r, v, { ratio: Math.min(ratio, 99), isReversal });
        }
        break;
      }
      case 'peak': {
        let maxV = 0, maxD = '', minV = 0, minD = '';
        for (const [d, val] of Object.entries(r.days)) {
          if (val > maxV) { maxV = val; maxD = d; }
          if (val < minV) { minV = val; minD = d; }
        }
        if (maxD === lastDay && maxV > 0) add(buying, r, maxV);
        if (minD === lastDay && minV < 0) add(selling, r, minV);
        break;
      }
      case 'followup': {
        let allPos = true, allNeg = true, sum = 0, count = 0;
        for (const d of last5) {
          const val = r.days[d];
          if (val === undefined || val === 0) { allPos = false; allNeg = false; break; }
          sum += val; count += 1;
          if (val < 0) allPos = false;
          if (val > 0) allNeg = false;
        }
        if (count === last5.length && count > 0) {
          if (allPos) add(buying, r, sum, { days: count });
          else if (allNeg) add(selling, r, sum, { days: count });
        }
        break;
      }
      case 'wholeMonth': {
        let allPos = true, allNeg = true, count = 0;
        for (const d of tradingDays) {
          const val = r.days[d];
          if (val === undefined || val === 0) { allPos = false; allNeg = false; break; }
          count += 1;
          if (val < 0) allPos = false;
          if (val > 0) allNeg = false;
        }
        if (count === tradingDays.length && count > 0) {
          if (allPos) add(buying, r, r.total, { days: count });
          else if (allNeg) add(selling, r, r.total, { days: count });
        }
        break;
      }
      case 'consistent75': {
        let pos = 0, neg = 0;
        for (const d of tradingDays) {
          const val = r.days[d];
          if (val === undefined || val === 0) continue;
          if (val > 0) pos += 1; else neg += 1;
        }
        const threshold = totalDays * 0.75;
        if (pos > threshold) add(buying, r, r.total, { days: pos, pct: Math.round((pos / totalDays) * 100) });
        else if (neg > threshold) add(selling, r, r.total, { days: neg, pct: Math.round((neg / totalDays) * 100) });
        break;
      }
      case 'dateWise': {
        if (!dateFilter) break;
        const v = r.days[dateFilter] || 0;
        if (v > 0) add(buying, r, v);
        else if (v < 0) add(selling, r, v);
        break;
      }
      default: break;
    }
  }

  if (key === 'dailySpike' || key === 'weeklySpike') {
    buying.sort((a, b) => b.ratio - a.ratio);
    selling.sort((a, b) => b.ratio - a.ratio);
  } else {
    buying.sort((a, b) => b.value - a.value);
    selling.sort((a, b) => a.value - b.value);
  }
  return { buying: buying.slice(0, TOP_N), selling: selling.slice(0, TOP_N) };
}

// ── Screener results table ─────────────────────────────────────────────────────
function ScreenerTable({ type, rows, year, month, showRatio, showDays, showPct }) {
  const [copied, setCopied] = useState(false);
  const isBuying = type === 'buying';

  const total = useMemo(
    () => rows.reduce((s, r) => s + Math.abs(r.value), 0),
    [rows]
  );

  const copySymbols = () => {
    if (!rows.length) return;
    const text = rows.map(r => `NSE:${r.symbol}`).join(',');
    const done = () => { setCopied(true); setTimeout(() => setCopied(false), 2000); };
    navigator.clipboard.writeText(text).then(done).catch(() => {
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      done();
    });
  };

  return (
    <div className={`mscr-table-card ${isBuying ? 'buying' : 'selling'}`}>
      <div className="mscr-table-head">
        <div className="mscr-table-title">
          <span className={`mscr-dir-icon ${isBuying ? 'buy' : 'sell'}`}>
            {isBuying ? '▲' : '▼'}
          </span>
          <span>{isBuying ? 'Buying Activity' : 'Selling Activity'}</span>
          <span className="mscr-table-count">{rows.length}</span>
        </div>
        <div className="mscr-table-head-right">
          <span className={`mscr-table-total ${isBuying ? 'pos' : 'neg'}`}>
            ₹{formatNumber(total)} L
          </span>
          <button
            className={`mscr-copy-btn${copied ? ' copied' : ''}`}
            onClick={copySymbols}
            disabled={!rows.length}
            title="Copy symbols for a TradingView watchlist"
          >
            {copied ? '✓ Copied' : '⧉ Copy'}
          </button>
        </div>
      </div>

      <div className="mscr-table-scroll">
        <table className="mscr-table">
          <thead>
            <tr>
              <th className="mscr-th-rank">#</th>
              <th>Symbol</th>
              {showRatio && <th className="mscr-th-meta">×Avg</th>}
              {showDays && <th className="mscr-th-meta">Days</th>}
              {showPct && <th className="mscr-th-meta">%</th>}
              <th className="mscr-th-val">CS Value</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={2 + (showRatio ? 1 : 0) + (showDays ? 1 : 0) + (showPct ? 1 : 0) + 1} className="mscr-empty">
                  No matching stocks
                </td>
              </tr>
            ) : (
              rows.map((r, i) => {
                const calUrl = `/cs-calendar?symbol=${encodeURIComponent(r.symbol)}&year=${year}&month=${month}&collection=${r.primary}`;
                return (
                  <tr key={r.symbol}>
                    <td className="mscr-td-rank">{i + 1}</td>
                    <td>
                      <div className="mscr-sym-cell">
                        <span className="mscr-sym">{r.symbol}</span>
                        <span className="mscr-tags">
                          {r.collections.map(c => (
                            <span key={c} className={`mscr-tag mscr-tag-${c}`}>{COL_TAG[c]}</span>
                          ))}
                        </span>
                        <a
                          href={`https://in.tradingview.com/chart/?symbol=NSE%3A${r.symbol}`}
                          target="_blank" rel="noopener noreferrer"
                          className="mscr-link tv" title={`${r.symbol} on TradingView`}
                        ><TvIcon /></a>
                        <a
                          href={calUrl}
                          target="_blank" rel="noopener noreferrer"
                          className="mscr-link cal" title={`CS Calendar for ${r.symbol}`}
                        ><CalIcon /></a>
                        {r.isReversal && <span className="mscr-rev" title="Reversal vs monthly trend">↺</span>}
                      </div>
                    </td>
                    {showRatio && (
                      <td className="mscr-td-meta">
                        {r.isReversal ? '↺' : `${r.ratio?.toFixed(1)}×`}
                      </td>
                    )}
                    {showDays && <td className="mscr-td-meta">{r.days ?? '—'}</td>}
                    {showPct && <td className="mscr-td-meta">{r.pct != null ? `${r.pct}%` : '—'}</td>}
                    <td className={`mscr-td-val ${isBuying ? 'pos' : 'neg'}`}>
                      ₹{formatNumber(Math.abs(r.value))} L
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function MarketScreener() {
  const [searchParams] = useSearchParams();

  const [year, setYear]   = useState(() => parseInt(searchParams.get('year'), 10) || new Date().getFullYear());
  const [month, setMonth] = useState(() => parseInt(searchParams.get('month'), 10) || (new Date().getMonth() + 1));
  const [screener, setScreener] = useState('daily');

  const [monthData, setMonthData] = useState({ rows: [], tradingDays: [] });
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');

  const [selectedDate, setSelectedDate] = useState('');
  const [filterCat, setFilterCat] = useState('all');
  const [sortMode, setSortMode]   = useState('rank');

  // ── Load full month raw data across all 3 collections ──────────────────────
  const loadMonth = useCallback(async () => {
    setLoading(true);
    setError('');
    const startDate = new Date(Date.UTC(year, month - 1, 1, 0, 0, 0, 0));
    const endDate   = new Date(Date.UTC(year, month, 0, 23, 59, 59, 999));

    try {
      const results = await Promise.all(
        COLLECTIONS.map(({ key }) =>
          api
            .get(`/market/${key}?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}&limit=50000`)
            .then(r => r.data)
            .catch(() => ({ data: [] }))
        )
      );

      const perCol = [];
      const tradingSet = new Set();

      results.forEach((resp, i) => {
        const { key } = COLLECTIONS[i];
        const map = {};
        (resp.data || []).forEach(rec => {
          const sym = rec.symbol;
          if (!sym || sym === 'MISSING_SYMBOL') return;
          const dateStr = toDateStr(rec.date);
          if (!dateStr) return;
          const val = parseVal(rec.csValue);
          tradingSet.add(dateStr);
          if (!map[sym]) map[sym] = { days: {}, total: 0 };
          map[sym].days[dateStr] = (map[sym].days[dateStr] || 0) + val;
          map[sym].total += val;
        });
        Object.entries(map).forEach(([sym, d]) =>
          perCol.push({ collection: key, symbol: sym, days: d.days, total: d.total })
        );
      });

      // Deduplicate a symbol across collections: keep the segment with the
      // largest absolute monthly CS value, but surface every category tag.
      const symMap = {};
      perCol.forEach(row => {
        const cur = symMap[row.symbol];
        if (!cur) {
          symMap[row.symbol] = {
            symbol: row.symbol,
            days: row.days,
            total: row.total,
            primary: row.collection,
            collections: [row.collection],
          };
        } else {
          cur.collections.push(row.collection);
          if (Math.abs(row.total) > Math.abs(cur.total)) {
            cur.days = row.days;
            cur.total = row.total;
            cur.primary = row.collection;
          }
        }
      });
      const rows = Object.values(symMap).map(r => ({
        ...r,
        collections: sortCollections(r.collections),
      }));

      const tradingDays = [...tradingSet].sort();
      setMonthData({ rows, tradingDays });
      setSelectedDate(prev => (prev && tradingSet.has(prev)) ? prev : (tradingDays[tradingDays.length - 1] || ''));
    } catch (err) {
      console.error('MarketScreener loadMonth error:', err);
      setError('Failed to load market data');
      setMonthData({ rows: [], tradingDays: [] });
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { loadMonth(); }, [loadMonth]);

  const { buying, selling } = useMemo(
    () => buildScreener(screener, monthData, selectedDate, filterCat),
    [screener, monthData, selectedDate, filterCat]
  );

  const sortView = useCallback((list) => {
    if (sortMode === 'csDesc') return [...list].sort((a, b) => Math.abs(b.value) - Math.abs(a.value));
    if (sortMode === 'csAsc')  return [...list].sort((a, b) => Math.abs(a.value) - Math.abs(b.value));
    if (sortMode === 'alpha')  return [...list].sort((a, b) => a.symbol.localeCompare(b.symbol));
    return list;
  }, [sortMode]);

  const buyingView  = useMemo(() => sortView(buying),  [buying, sortView]);
  const sellingView = useMemo(() => sortView(selling), [selling, sortView]);

  const activeScreener = SCREENERS.find(s => s.key === screener) || SCREENERS[0];
  const showRatio = screener === 'dailySpike' || screener === 'weeklySpike';
  const showDays  = screener === 'followup' || screener === 'wholeMonth';
  const showPct   = screener === 'consistent75';

  const footerNote = (() => {
    const mLabel = `${MONTHS_FULL[month - 1]} ${year}`;
    switch (screener) {
      case 'daily':        return `Last trading day · ${mLabel}`;
      case 'weekly':       return `Current week (Mon–Sun) · ${mLabel}`;
      case 'monthly':      return `Full month · ${mLabel}`;
      case 'dailySpike':   return `≥${SPIKE_MULT}× daily avg · Last day vs ${mLabel} avg`;
      case 'weeklySpike':  return `≥${SPIKE_MULT}× daily avg · Current week (Mon–Sun) vs ${mLabel} avg`;
      case 'peak':         return `Month high/low day = last trading day · ${mLabel}`;
      case 'followup':     return `Same direction every day · Last 5 days · ${mLabel}`;
      case 'wholeMonth':   return `Same direction every trading day · ${mLabel}`;
      case 'consistent75': return `Same direction on >75% of days · ${mLabel}`;
      case 'dateWise':     return `Selected date${selectedDate ? ` · ${selectedDate}` : ''}`;
      default:             return mLabel;
    }
  })();

  return (
    <AppLayout pageBadge="Market Screener">
      <div className="mscr-root">

        {/* ── Left: screener list ─────────────────────────────────── */}
        <aside className="mscr-panel">
          <div className="mscr-panel-head">
            <span className="mscr-panel-title">🎯 Screeners</span>
          </div>

          <div className="mscr-controls">
            <div className="mscr-ctrl-group">
              <span className="mscr-ctrl-label">Year</span>
              <select
                className="mscr-select"
                aria-label="Year"
                value={year}
                onChange={e => setYear(parseInt(e.target.value, 10))}
              >
                {[...new Set([...YEARS, year])].sort((a, b) => a - b).map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            <div className="mscr-ctrl-group">
              <span className="mscr-ctrl-label">Month</span>
              <select
                className="mscr-select"
                aria-label="Month"
                value={month}
                onChange={e => setMonth(parseInt(e.target.value, 10))}
              >
                {MONTHS_SHORT.map((m, i) => (
                  <option key={m} value={i + 1}>{m}</option>
                ))}
              </select>
            </div>
          </div>

          {screener === 'dateWise' && (
            <div className="mscr-controls mscr-date-row">
              <div className="mscr-ctrl-group" style={{ width: '100%' }}>
                <span className="mscr-ctrl-label">Date</span>
                <input
                  type="date"
                  className="mscr-select mscr-date-input"
                  aria-label="Screener date"
                  value={selectedDate}
                  min={monthData.tradingDays[0] || undefined}
                  max={monthData.tradingDays[monthData.tradingDays.length - 1] || undefined}
                  onChange={e => setSelectedDate(e.target.value)}
                />
              </div>
            </div>
          )}

          <div className="mscr-screener-list">
            {SCREENERS.map(s => (
              <button
                key={s.key}
                className={`mscr-screener-item${screener === s.key ? ' active' : ''}`}
                onClick={() => setScreener(s.key)}
              >
                <span className="mscr-screener-icon">{s.icon}</span>
                <span className="mscr-screener-text">
                  <span className="mscr-screener-label">{s.label}</span>
                  <span className="mscr-screener-desc">{s.desc}</span>
                </span>
              </button>
            ))}
          </div>
        </aside>

        {/* ── Main: results ───────────────────────────────────────── */}
        <main className="mscr-main">
          <div className="mscr-main-head">
            <div className="mscr-main-title">
              <span className="mscr-main-icon">{activeScreener.icon}</span>
              <div className="mscr-main-title-text">
                <span className="mscr-main-label">{activeScreener.label}</span>
                <span className="mscr-main-sub">{activeScreener.desc}</span>
              </div>
            </div>
            <div className="mscr-main-note">{footerNote}</div>
          </div>

          {!loading && !error && (
            <div className="mscr-toolbar">
              <div className="mscr-tb-group">
                <span className="mscr-tb-label">Category</span>
                {[['all', 'All'], ['nifty50', 'N50'], ['fo', 'FO'], ['cash', 'CASH']].map(([k, l]) => (
                  <button
                    key={k}
                    className={`mscr-tb-btn${filterCat === k ? ' active' : ''}`}
                    onClick={() => setFilterCat(k)}
                  >{l}</button>
                ))}
              </div>
              <div className="mscr-tb-group">
                <span className="mscr-tb-label">Sort</span>
                {[['rank', 'Rank'], ['csDesc', 'CS ↓'], ['csAsc', 'CS ↑'], ['alpha', 'A–Z']].map(([k, l]) => (
                  <button
                    key={k}
                    className={`mscr-tb-btn${sortMode === k ? ' active' : ''}`}
                    onClick={() => setSortMode(k)}
                  >{l}</button>
                ))}
              </div>
            </div>
          )}

          {loading ? (
            <div className="mscr-status">
              <div className="mscr-spinner" />
              Loading market data…
            </div>
          ) : error ? (
            <div className="mscr-status mscr-error">{error}</div>
          ) : (
            <div className="mscr-tables">
              <ScreenerTable
                type="buying"  rows={buyingView}  year={year} month={month}
                showRatio={showRatio} showDays={showDays} showPct={showPct}
              />
              <ScreenerTable
                type="selling" rows={sellingView} year={year} month={month}
                showRatio={showRatio} showDays={showDays} showPct={showPct}
              />
            </div>
          )}
        </main>
      </div>
    </AppLayout>
  );
}
